/* ============================================================
   ShopPage — public seller product catalog (grid + filters).
   3d4c488f: MakerWorld-style category chips (roots + subs),
   price-range filter and server-side pagination via /products/page.
   ============================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { Material } from '../types';
import type { ProductSummary } from '../types/product';
import type { ProductCategoryNode } from '../types/category';
import { formatRub } from '../types/product';
import { resolveMediaUrl } from '../api/client';
import { summaryCoverUrl } from './mediaGalleryView';
import { buildCategoryFilterView, resolveBrowseCategoryId } from './shopCategoryView';
import { ListPager } from './ListPager';

const PAGE_SIZE = 24;

interface Props {
  onOpenProduct: (id: string) => void;
}

export function ShopPage({ onOpenProduct }: Props) {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [categories, setCategories] = useState<ProductCategoryNode[]>([]);
  const [materialId, setMaterialId] = useState<string>('');
  const [categoryRoot, setCategoryRoot] = useState<string | null>(null);
  const [categorySub, setCategorySub] = useState<string | null>(null);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categoryId = resolveBrowseCategoryId(categoryRoot, categorySub);
  const categoryView = useMemo(
    () => buildCategoryFilterView(categories, categoryRoot, categorySub),
    [categories, categoryRoot, categorySub],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.getProductPage({
        materialId: materialId || undefined,
        categoryId,
        q: query.trim() || undefined,
        minPriceRub: minPrice.trim() ? Number(minPrice) : undefined,
        maxPriceRub: maxPrice.trim() ? Number(maxPrice) : undefined,
        offset,
        limit: PAGE_SIZE,
      });
      setProducts(page.items);
      setTotal(page.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [materialId, categoryId, query, minPrice, maxPrice, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api.getMaterials().then(setMaterials).catch(() => {});
    api.getCategories().then(setCategories).catch(() => {});
  }, []);

  // filter changes restart from the first page
  const changeFilters = (apply: () => void) => {
    apply();
    setOffset(0);
  };

  const inputStyle = {
    height: 44,
    padding: '0 var(--space-md)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    background: 'var(--color-surface)',
    fontSize: 'var(--fs-body)',
  } as const;

  const chipStyle = (active: boolean) => ({
    height: 36,
    padding: '0 var(--space-md)',
    borderRadius: 18,
    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-separator)'}`,
    background: active ? 'var(--color-primary)' : 'var(--color-surface)',
    color: active ? '#fff' : 'var(--color-text-primary)',
    fontSize: 'var(--fs-caption)',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
  }) as const;

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
      {/* Heading */}
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em' }}>
          Магазин
        </h1>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Готовые модели от проверенных исполнителей — фиксированная цена, без ожидания расчёта
        </p>
      </div>

      {/* Category chips (3d4c488f) */}
      {categoryView.roots.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
          <button
            style={chipStyle(categoryRoot === null)}
            onClick={() => changeFilters(() => { setCategoryRoot(null); setCategorySub(null); })}
          >
            Все категории
          </button>
          {categoryView.roots.map((root) => (
            <button
              key={root.id}
              style={chipStyle(root.active)}
              onClick={() => changeFilters(() => { setCategoryRoot(root.active ? null : root.id); setCategorySub(null); })}
            >
              {root.name}
              {root.count > 0 && <span style={{ opacity: 0.7 }}> · {root.count}</span>}
            </button>
          ))}
        </div>
      )}
      {categoryView.subs.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
          {categoryView.subs.map((sub) => (
            <button
              key={sub.id}
              style={chipStyle(sub.active)}
              onClick={() => changeFilters(() => setCategorySub(sub.active ? null : sub.id))}
            >
              {sub.name}
              {sub.count > 0 && <span style={{ opacity: 0.7 }}> · {sub.count}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-lg)' }}>
        <input
          type="search"
          placeholder="Поиск по названию…"
          value={query}
          onChange={(e) => changeFilters(() => setQuery(e.target.value))}
          style={{ ...inputStyle, flex: '1 1 220px' }}
        />
        <select
          value={materialId}
          onChange={(e) => changeFilters(() => setMaterialId(e.target.value))}
          style={inputStyle}
        >
          <option value="">Все материалы</option>
          {materials.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          placeholder="₽ от"
          value={minPrice}
          onChange={(e) => changeFilters(() => setMinPrice(e.target.value))}
          style={{ ...inputStyle, width: 90 }}
        />
        <input
          type="number"
          min={0}
          placeholder="₽ до"
          value={maxPrice}
          onChange={(e) => changeFilters(() => setMaxPrice(e.target.value))}
          style={{ ...inputStyle, width: 90 }}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'grid', gap: 'var(--space-md)', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton" style={{ height: 240, borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : error ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={() => void load()} style={{ marginTop: 'var(--space-md)' }}>
            Повторить
          </button>
        </div>
      ) : products.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Пока пусто</p>
          <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            По этим фильтрам ничего не нашлось — сбросьте их или закажите печать своей модели
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 'var(--space-md)', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {products.map((p) => (
              <button
                key={p.productId}
                onClick={() => onOpenProduct(p.productId)}
                className="card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-xs)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: 'var(--space-md)',
                  transition: 'transform 0.15s ease',
                }}
              >
                {/* ME-3: cover image when the product has gallery media */}
                {(() => {
                  const cover = summaryCoverUrl(p);
                  return cover ? (
                    <img
                      src={resolveMediaUrl(cover)}
                      alt={p.title}
                      loading="lazy"
                      style={{
                        height: 120,
                        borderRadius: 'var(--radius-md)',
                        objectFit: 'cover',
                        width: '100%',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        height: 120,
                        borderRadius: 'var(--radius-md)',
                        background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 'var(--fs-h1)',
                        fontWeight: 'var(--fw-bold)',
                      }}
                    >
                      {p.title.slice(0, 1).toUpperCase()}
                    </div>
                  );
                })()}
                <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-body)' }}>{p.title}</div>
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  {p.providerName} · {p.materialName} · {p.color}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-xs)', marginTop: 'auto' }}>
                  <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
                    {formatRub(p.priceRub)}
                  </span>
                  {p.compareAtPriceRub && (
                    <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)', textDecoration: 'line-through' }}>
                      {formatRub(p.compareAtPriceRub)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  {p.stockLeft === null ? 'В наличии' : p.stockLeft > 0 ? `Осталось: ${p.stockLeft}` : 'Нет в наличии'}
                  {' · '}срок {p.productionDays} дн.
                </div>
              </button>
            ))}
          </div>
          <ListPager
            total={total}
            offset={offset}
            limit={PAGE_SIZE}
            onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            onNext={() => setOffset(offset + PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
